import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';


export default function Batches() {
  const navigate = useNavigate();
  const [showNewBatchModal, setShowNewBatchModal] = useState(false);
  const [batches, setBatches] = useState([]);
  const [allLiveSchedules, setAllLiveSchedules] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [liveClassViewMode, setLiveClassViewMode] = useState('all');
  const [selectedLiveDate, setSelectedLiveDate] = useState(new Date());
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingTeachers, setLoadingTeachers] = useState(true);

  const scheduleConflicts = useMemo(() => {
    const conflicts = [];
    const timeBlocks = {}; 
    
    allLiveSchedules.forEach(sch => {
       if (!sch.time || !sch.rawTopic) return;
       const key = `${sch.rawTopic}|${sch.time}`;
       if (!timeBlocks[key]) {
         timeBlocks[key] = { teachers: {}, batches: {} };
       }
       
       if (sch.teacher && sch.teacher.trim() !== '' && sch.teacher !== 'Unassigned') {
         if (!timeBlocks[key].teachers[sch.teacher]) timeBlocks[key].teachers[sch.teacher] = new Set();
         timeBlocks[key].teachers[sch.teacher].add(sch.batch || 'Unknown Batch');
       }
       
       if (sch.batch) {
         if (!timeBlocks[key].batches[sch.batch]) timeBlocks[key].batches[sch.batch] = new Set();
         const entry = sch.teacher ? `${sch.subject || 'Class'} (${sch.teacher})` : (sch.subject || 'Unknown Subject');
         timeBlocks[key].batches[sch.batch].add(entry);
       }
    });

    Object.keys(timeBlocks).forEach(key => {
       const [date, time] = key.split('|');
       const block = timeBlocks[key];
       
       Object.keys(block.teachers).forEach(teacher => {
         const mappedBatches = Array.from(block.teachers[teacher]);
         if (mappedBatches.length > 1) {
           conflicts.push({
             id: Math.random().toString(),
             type: 'Teacher Overlap',
             title: `Double-Booked: ${teacher}`,
             message: `Scheduled in ${mappedBatches.join(' and ')} simultaneously.`,
             date: date,
             time: time,
             severity: 'high'
           });
         }
       });

       Object.keys(block.batches).forEach(batch => {
         const mappedEntities = Array.from(block.batches[batch]);
         if (mappedEntities.length > 1 && batch !== 'Unknown Batch') {
           conflicts.push({
             id: Math.random().toString(),
             type: 'Batch Overlap',
             title: `Clash in ${batch}`,
             message: `Multiple classes (${mappedEntities.join(', ')}) scheduled simultaneously.`,
             date: date,
             time: time,
             severity: 'critical'
           });
         }
       });
    });
    
    return conflicts.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  }, [allLiveSchedules]);
  
  const handleStartLiveClass = async (cls) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT' };
      
      // We need the schedule index and batch ID. 
      // In the mapping below, I'll ensure these are preserved.
      if (!cls.batch || cls.scheduleIndex === undefined) {
        toast.error("Cannot identify this class session.");
        return;
      }

      const res = await axios.post(`${API_BASE_URL}/api/live/start/${cls.batch}/${cls.scheduleIndex}`, {}, { headers });
      const updatedSchedule = res.data;
      
      // Redirect to the live meeting page
      const roomName = updatedSchedule.meetingUrl.split('/').pop();
      navigate(`/:instituteId/lms/live-class/${cls.batch}/${roomName}`);
      toast.success("Live class started!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to start live class.");
    }
  };

  const handleJoinLiveClass = (cls) => {
    if (!cls.meetingUrl) {
      toast.error("Meeting link not found.");
      return;
    }
    const roomName = cls.meetingUrl.split('/').pop();
    navigate(`/:instituteId/lms/live-class/${cls.batch}/${roomName}`);
  };

  const handleDeleteSchedule = async (id, subject) => {
    if (!window.confirm(`Are you sure you want to delete the "${subject}" schedule? This cannot be undone.`)) return;

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT' };
      await axios.delete(`${API_BASE_URL}/api/batches/schedule/${id}`, { headers });
      
      toast.success("Schedule deleted successfully.");
      fetchBatches(); 
      fetchTeachers();
    } catch (err) {
      console.error("Failed to delete schedule", err);
      toast.error("Error deleting schedule.");
    }
  };

  const renderClassCard = (cls, idx) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isTeacher = user.role === 'teacher' || user.role === 'institute' || user.role === 'admin';
    const isLive = cls.status === 'Ongoing' || cls.isLive;

    return (
      <div key={idx} className={`relative p-4 md:p-5 rounded-2xl border ${isLive ? 'bg-violet-900/20 border-violet-500/30' : 'bg-slate-900/40 border-slate-800'} flex flex-col md:flex-row md:items-center justify-between gap-4 overflow-hidden group hover:border-slate-700 transition-colors`}>
        {isLive && (
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/10 blur-[40px] rounded-full pointer-events-none"></div>
        )}
        <div className="flex items-center gap-4 md:gap-5 relative z-10 w-full md:w-auto">
          <div className={`w-16 h-16 md:w-20 md:h-16 rounded-xl flex flex-col items-center justify-center shrink-0 border shadow-inner px-1 ${isLive ? 'bg-violet-500/10 border-violet-500/20 text-violet-400' : 'bg-slate-800 border-slate-700 text-slate-400'}`}>
            <span className="font-bold text-sm tracking-tight">{cls.time ? (cls.time.includes('-') ? cls.time.split('-')[0].trim() : cls.time.split(' ')[0]) : '--:--'}</span>
            {cls.time && cls.time.includes('-') && (
              <span className="text-[10px] font-bold text-emerald-400/80 truncate w-full text-center mt-0.5">to {cls.time.split('-')[1].trim()}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 truncate max-w-[120px]">{cls.subject || 'Subject'}</span>
              {cls.batch && liveClassViewMode !== 'batch' && <span className="text-[9px] font-bold text-amber-500/80 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap">{cls.batch}</span>}
            </div>
            <h3 className={`font-semibold text-base md:text-lg truncate ${isLive ? 'text-violet-200' : 'text-slate-200'}`}>
              {cls.topic || 'Class Session'}
            </h3>
          </div>
        </div>
        <div className="flex items-center justify-between md:justify-end gap-4 md:gap-6 relative z-10 w-full md:w-auto mt-2 md:mt-0 pt-2 md:pt-0 border-t border-slate-800/60 md:border-0">
          <div className="text-left md:text-right">
            <p className={`text-sm font-medium ${liveClassViewMode === 'teacher' ? 'text-emerald-400' : 'text-slate-400'}`}>{cls.teacher || 'Unassigned'}</p>
          </div>
          
          {isLive ? (
            <button 
              onClick={() => handleJoinLiveClass(cls)}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all w-full sm:w-auto active:scale-95 bg-violet-500 hover:bg-violet-600 text-white shadow-lg shadow-violet-500/20"
            >
              Join Now
            </button>
          ) : (
            isTeacher ? (
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <button 
                  onClick={() => handleStartLiveClass(cls)}
                  className="px-4 py-2 rounded-xl text-sm font-medium transition-all w-full sm:w-auto active:scale-95 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30"
                >
                  Start Class
                </button>
                <button 
                  onClick={() => handleAttendanceClick(cls)}
                  className="px-4 py-2 rounded-xl text-sm font-medium transition-all w-full sm:w-auto active:scale-95 bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 border border-violet-500/30 flex items-center justify-center gap-1.5"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><polyline points="17 11 19 13 23 9"></polyline></svg>
                  Attendance
                </button>
              </div>
            ) : (
              <button className="px-4 py-2 rounded-xl text-sm font-medium transition-all w-full sm:w-auto active:scale-95 bg-slate-800 text-slate-500 border border-slate-700 cursor-not-allowed">
                Upcoming
              </button>
            )
          )}
          
          {(user.role === 'institute' || user.role === 'SUPERADMIN' || user.role === 'admin' || user.role === 'ADMIN') && (
            <button 
              onClick={() => cls.id ? handleDeleteSchedule(cls.id, cls.subject) : alert("This is a legacy schedule without a unique ID. Please re-upload your timetable to enable deletion.")}
              className="p-2 text-slate-500 hover:text-red-400 transition-colors"
              title="Delete Schedule"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderLoadingState = (message) => (
    <div className="flex flex-col items-center justify-center py-20 animate-pulse">
      <div className="w-12 h-12 border-4 border-violet-500/20 border-t-violet-500 rounded-full animate-spin mb-4"></div>
      <p className="text-slate-400 font-medium tracking-wide">{message}...</p>
    </div>
  );

  // Batch Details Modal States
  const [selectedBatchDetails, setSelectedBatchDetails] = useState(null);
  const [batchStudents, setBatchStudents] = useState([]);
  const [batchTeachers, setBatchTeachers] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  const [selectedScheduleBatch, setSelectedScheduleBatch] = useState(null);
  const [selectedScheduleWeekDate, setSelectedScheduleWeekDate] = useState(new Date());
  
  const getDateForDayInSelectedWeek = (dayName, baseDate) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    let d = new Date(baseDate);
    let currentDay = d.getDay();
    let targetDay = days.indexOf(dayName);
    let adjCurr = currentDay === 0 ? 6 : currentDay - 1;
    let adjTarg = targetDay === 0 ? 6 : targetDay - 1;
    d.setDate(d.getDate() + (adjTarg - adjCurr));
    return d;
  };
  
  const [editableSchedule, setEditableSchedule] = useState({
    Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [], Sunday: []
  });

  useEffect(() => {
    if (selectedScheduleBatch) {
      const newSchedule = {
        Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [], Sunday: []
      };
      
      const batchSchedules = selectedScheduleBatch.masterSchedule || [];
      
      // Calculate start and end of the currently selected week for filtering
      const monday = getDateForDayInSelectedWeek('Monday', selectedScheduleWeekDate);
      monday.setHours(0, 0, 0, 0);
      const sunday = getDateForDayInSelectedWeek('Sunday', selectedScheduleWeekDate);
      sunday.setHours(23, 59, 59, 999);

      batchSchedules.forEach(sch => {
        // Try to parse the schedule date to see if it belongs to this week
        let isInWeek = true;
        if (sch.date) {
          const schDate = new Date(sch.date);
          isInWeek = schDate >= monday && schDate <= sunday;
        }

        if (isInWeek && newSchedule[sch.dayOfWeek]) {
          newSchedule[sch.dayOfWeek].push({
            id: sch.id ? sch.id.toString() : (Date.now().toString() + Math.random().toString()),
            time: sch.startTime && sch.endTime ? `${sch.startTime} - ${sch.endTime}` : (sch.time || ""),
            date: sch.date,
            subject: sch.subject,
            teacher: sch.teacherEmail
          });
        }
      });
      
      Object.keys(newSchedule).forEach(day => {
        newSchedule[day].sort((a,b) => (a.time || "").localeCompare(b.time || ""));
      });
      
      setEditableSchedule(newSchedule);
    }
  }, [selectedScheduleBatch, selectedScheduleWeekDate]);

  const handleSaveSchedule = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT' };
      
      // Transform local editableSchedule into explicit DTO with exact date, startTime, and endTime
      const extendedPayload = {};
      Object.entries(editableSchedule).forEach(([day, classes]) => {
          const d = getDateForDayInSelectedWeek(day, selectedScheduleWeekDate);
          const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          
          extendedPayload[day] = classes.map(cls => {
              const timeParts = (cls.time || "").split(" - ");
              return {
                  ...cls,
                  date: dateStr,
                  startTime: timeParts[0] || "",
                  endTime: timeParts[1] || ""
              };
          });
      });

      await axios.post(`${API_BASE_URL}/api/batches/${selectedScheduleBatch.batchIdStr}/schedule`, extendedPayload, { headers });
      
      // Sync local UI explicitly bypassing full component reloads
      setBatches(batches.map(b => b.batchIdStr === selectedScheduleBatch.batchIdStr ? { 
          ...b, 
          masterSchedule: Object.entries(extendedPayload).flatMap(([dayOfWeek, classes]) => 
              classes.map(cls => ({ dayOfWeek, ...cls, teacherEmail: cls.teacher }))
          ) 
      } : b));
      toast("Master Schedule securely saved to the Database!");
    } catch (err) {
      console.error(err);
      toast("Failed to sync the timetable properly. See server logs.");
    }
  };

  const handleScheduleChange = (day, idx, field, value) => {
    setEditableSchedule(prev => ({
      ...prev,
      [day]: prev[day].map((item, i) => i === idx ? { ...item, [field]: value } : item)
    }));
  };

  const handleExportSchedule = () => {
    const batchId = selectedScheduleBatch?.batchIdStr || "Batch";
    let csvContent = `Date,DayOfWeek,StartTime,EndTime,Subject,TeacherEmail,BatchID\n`;

    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].forEach(day => {
      if (editableSchedule[day]) {
        let d = getDateForDayInSelectedWeek(day, selectedScheduleWeekDate);
        let dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        editableSchedule[day].forEach(slot => {
          if (slot.time || slot.subject || slot.teacher) {
            const timeParts = (slot.time || "").split(" - ");
            const startTime = timeParts[0] || "";
            const endTime = timeParts[1] || "";
            const subject = slot.subject || "";
            const teacher = slot.teacher || "";
            
            csvContent += `${dateStr},${day},${startTime},${endTime},"${subject}","${teacher}","${batchId}"\n`;
          }
        });
      }
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${selectedScheduleBatch?.batchIdStr || "schedule"}_timetable.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const [batchName, setBatchName] = useState('');
  const [hod, setHod] = useState('');
  const [pipeline, setPipeline] = useState('');
  
  const [allStudents, setAllStudents] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [selectedAddStudent, setSelectedAddStudent] = useState('');
  const [selectedAddTeacher, setSelectedAddTeacher] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleFile, setScheduleFile] = useState(null);
  const [uploadingSchedule, setUploadingSchedule] = useState(false);

  const [studentFile, setStudentFile] = useState(null);
  const [teacherFile, setTeacherFile] = useState(null);
  const [timetableFile, setTimetableFile] = useState(null);
  
  const [dragActive, setDragActive] = useState({ student: false, teacher: false, timetable: false });

  // Attendance States
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedAttendanceBatch, setSelectedAttendanceBatch] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceRecords, setAttendanceRecords] = useState({}); // email -> { status, remarks }
  const [submittingAttendance, setSubmittingAttendance] = useState(false);
  const [attendanceStudents, setAttendanceStudents] = useState([]);

  // AI Schedule States
  const [showAiModal, setShowAiModal] = useState(false);
  const [showAiPreviewModal, setShowAiPreviewModal] = useState(false);
  const [aiFullResults, setAiFullResults] = useState(null);
  const [aiConstraints, setAiConstraints] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [aiStartDate, setAiStartDate] = useState(() => {
    const d = new Date();
    // Default to next Monday
    d.setDate(d.getDate() + ((1 + 7 - d.getDay()) % 7 || 7));
    return d.toISOString().split('T')[0];
  });

  const handleGenerateAiSchedule = async () => {
    setIsGenerating(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT' };
      
      const res = await axios.post(`${API_BASE_URL}/api/batches/generate-schedule`, { constraints: aiConstraints }, { headers });
      const generatedData = res.data;

      // Map day name to dates
      const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const baseDate = new Date(aiStartDate);
      
      // Store full results for preview
      const fullMapped = {};
      Object.entries(generatedData).forEach(([day, classes]) => {
        const dayIndex = daysOrder.indexOf(day);
        const targetDate = new Date(baseDate);
        if (dayIndex !== -1) targetDate.setDate(baseDate.getDate() + dayIndex);
        const dateStr = targetDate.toISOString().split('T')[0];

        fullMapped[day] = classes.map(cls => ({
          ...cls,
          date: dateStr,
          id: 'ai-' + Math.random().toString(36).substr(2, 9)
        }));
      });

      setAiFullResults(fullMapped);
      setShowAiModal(false);
      setShowAiPreviewModal(true);

    } catch (err) {
      console.error(err);
      toast.error("Failed to generate AI schedule. Check console/backend.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApplyAllAiSchedules = async () => {
    setIsApplyingAll(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT' };
      
      // We need to group by batch for the bulk endpoint
      const groupedByBatch = {};
      Object.entries(aiFullResults).forEach(([day, classes]) => {
        classes.forEach(cls => {
          // Resolve batch string to canonical ID
          const batchMatch = batches.find(b => 
            b.batchIdStr?.toLowerCase() === cls.batch?.toString().toLowerCase() ||
            b.name?.toLowerCase() === cls.batch?.toString().toLowerCase()
          );
          const bId = batchMatch ? batchMatch.batchIdStr : cls.batch;

          if (!groupedByBatch[bId]) groupedByBatch[bId] = {};
          if (!groupedByBatch[bId][day]) groupedByBatch[bId][day] = [];
          groupedByBatch[bId][day].push({
            ...cls,
            teacher: cls.teacher,
            startTime: cls.startTime,
            endTime: cls.endTime,
            subject: cls.subject
          });
        });
      });

      await axios.post(`${API_BASE_URL}/api/batches/bulk-schedule`, groupedByBatch, { headers });
      toast.success("AI Master Schedule applied to all batches!");
      setShowAiPreviewModal(false);
      fetchBatches(); // Refresh list to see "Tomorrow Scheduled" updates
    } catch (err) {
      console.error(err);
      const errorMessage = err?.response?.data?.message || err?.message || "Check backend console";
      toast.error(`Bulk apply failed: ${errorMessage}`);
    } finally {
      setIsApplyingAll(false);
    }
  };

  const fetchTeachers = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
    const headers = { 'X-Tenant-ID': tenantId };
    setLoadingTeachers(true);

    axios.get(API_BASE_URL + '/api/teachers', { headers })
      .then(res => {
        setAllTeachers(res.data); // Synchronize global select dropdown options immediately
        let schedules = [];

        res.data.forEach(t => {
          if (t.todaysSchedule) {
            t.todaysSchedule.forEach(sch => {
                schedules.push({
                  time: sch.timeSlot,
                  subject: sch.subject,
                  topic: sch.topic || "Class Session",
                  rawTopic: sch.topic,
                  teacher: t.name,
                  batch: sch.batchName,
                  type: "Live App",
                  status: sch.status || "Upcoming"
                });
            });
          }
        });
        
        // Sort by time (converting HH:MM AM/PM to 24H for comparison)
        schedules.sort((a,b) => {
           const get24H = (timeStr) => {
             if (!timeStr) return '00:00';
             let parts = timeStr.split(' - ')[0].trim().split(' ');
             if (parts.length < 2) return timeStr;
             const time = parts[0];
             const modifier = parts[1];
             let [hours, minutes] = time.split(':');
             if (hours === '12') hours = '00';
             if (modifier === 'PM') hours = String(parseInt(hours, 10) + 12);
             return `${hours.padStart(2, '0')}:${minutes}`;
           };
           return get24H(a.time).localeCompare(get24H(b.time));
        });
        
        setAllLiveSchedules(schedules);
      })
      .catch(err => console.error("Failed to fetch live classes details", err))
      .finally(() => setLoadingTeachers(false));
  };

  useEffect(() => {
    const year = selectedLiveDate.getFullYear();
    const month = String(selectedLiveDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedLiveDate.getDate()).padStart(2, '0');
    const targetDateStr = `${year}-${month}-${day}`;
    
    // 1. Get schedules from teacher profiles (via api/teachers)
    const teacherSchedules = (Array.isArray(allLiveSchedules) ? allLiveSchedules : []).filter(sch => {
      return !sch.rawTopic || sch.rawTopic.includes(targetDateStr);
    }).map(sch => ({
      ...sch,
      topic: "Scheduled Lecture"
    }));

    // 2. Get schedules directly from batch master schedules (Robust fallback)
    const batchSchedules = [];
    batches.forEach(b => {
      if (b.masterSchedule) {
        b.masterSchedule.forEach((sch, sIdx) => {
          if (sch.date === targetDateStr) {
             batchSchedules.push({
               id: sch.id, // Capture the ID for deletion from master source
               scheduleIndex: sIdx, // Vital for the start API
               time: sch.time || `${sch.startTime} - ${sch.endTime}`,
               subject: sch.subject,
               teacher: sch.teacherEmail || "Staff",
               batch: b.batchIdStr,
               type: "Live App",
               status: sch.isLive ? "Ongoing" : "Upcoming",
               isLive: sch.isLive,
               meetingUrl: sch.meetingUrl,
               topic: "Scheduled Lecture"
             });
          }
        });
      }
    });

    // Merge and deduplicate by (batch + time)
    const combined = [...teacherSchedules];
    batchSchedules.forEach(bs => {
      const exists = combined.find(c => c.batch === bs.batch && c.time === bs.time);
      if (!exists) {
        combined.push(bs);
      } else {
        // Enrich the existing object from teacher profile if it matches a master schedule slot
        exists.scheduleIndex = bs.scheduleIndex;
        exists.isLive = bs.isLive;
        exists.meetingUrl = bs.meetingUrl;
        if (bs.isLive) exists.status = "Ongoing";
      }
    });
    
    setLiveClasses(combined);
  }, [allLiveSchedules, batches, selectedLiveDate]);

  const changeLiveDate = (daysOffset) => {
    const newDate = new Date(selectedLiveDate);
    newDate.setDate(newDate.getDate() + daysOffset);
    setSelectedLiveDate(newDate);
  };


  useEffect(() => {
    fetchBatches();
    fetchTeachers();
  }, []);
  const fetchBatches = async () => {
    setLoadingBatches(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      const res = await axios.get(API_BASE_URL + '/api/batches', { headers });
      setBatches(res.data);
    } catch (err) {
      console.error("Failed to fetch batches from server", err);
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleDeleteBatch = async (batch) => {
    if (!window.confirm(`Are you sure you want to delete the batch "${batch.name}"? Students will be unassigned, but their profiles will remain. This action is permanent.`)) {
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = { 'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT' };
      
      await axios.delete(`${API_BASE_URL}/api/batches/${batch.id}`, { headers });
      toast.success("Batch deleted successfully");
      fetchBatches(); // Refresh list
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete batch. Check server logs.");
    }
  };

  const handleBatchClick = async (batch) => {
    setSelectedBatchDetails(batch);
    setDetailsLoading(true);
    setSelectedAddStudent('');
    setSelectedAddTeacher('');
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };

      const [studentsRes, teachersRes, allStuRes, allTchRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/batches/${batch.batchIdStr}/students`, { headers }),
        axios.get(`${API_BASE_URL}/api/batches/${batch.batchIdStr}/teachers`, { headers }),
        axios.get(`${API_BASE_URL}/api/students`, { headers }),
        axios.get(`${API_BASE_URL}/api/teachers`, { headers })
      ]);
      setBatchStudents(Array.isArray(studentsRes.data) ? studentsRes.data : []);
      setBatchTeachers(Array.isArray(teachersRes.data) ? teachersRes.data : []);
      setAllStudents(Array.isArray(allStuRes.data) ? allStuRes.data : []);
      setAllTeachers(Array.isArray(allTchRes.data) ? allTchRes.data : []);
    } catch (err) {
      console.error("Failed to fetch batch assignments", err);
      // Fallback to empty arrays on error
      setBatchStudents([]);
      setBatchTeachers([]);
      setAllStudents([]);
      setAllTeachers([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleAddMember = async (type) => {
    if (!selectedBatchDetails) return;
    const isStudent = type === 'student';
    const memberId = isStudent ? selectedAddStudent : selectedAddTeacher;
    if (!memberId) return;

    setActionLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/batches/${selectedBatchDetails.batchIdStr}/${isStudent ? 'students' : 'teachers'}/add/${memberId}`);
      await handleBatchClick(selectedBatchDetails); // Refresh side panel details
      await fetchBatches(); // Refresh main dashboard list (crucial for card counters/status)
    } catch (err) {
      toast("Failed to assign member to batch.");
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (type, memberId) => {
    if (!selectedBatchDetails || !window.confirm("Are you sure you want to unassign this member from the batch?")) return;
    const isStudent = type === 'student';
    
    setActionLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/batches/${selectedBatchDetails.batchIdStr}/${isStudent ? 'students' : 'teachers'}/remove/${memberId}`);
      await handleBatchClick(selectedBatchDetails); // Refresh side panel details
      await fetchBatches(); // Refresh main dashboard list
    } catch (err) {
      toast("Failed to unassign member.");
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  // Generate mock CSVs that open in Excel
  const downloadSample = (type) => {
    let csvContent = "";
    let filename = "";
    
    if (type === 'student') {
      csvContent = "Student_ID,Full_Name,Email_Address,Phone_Number,Target_Exam\nSTU-001,Amit Kumar,amit@example.com,9876543210,JEE\nSTU-002,Priya Singh,priya@example.com,9876543211,NEET";
      filename = "Sample_Student_Roster.csv";
    } else if (type === 'teacher') {
      csvContent = "Teacher_Email,Full_Name,Primary_Subject,Role\ndr.verma@example.com,Dr. S. K. Verma,Physics,HOD\nprof.singh@example.com,Prof. Amit Singh,Math,Senior Faculty";
      filename = "Sample_Teacher_Assignment.csv";
    } else if (type === 'timetable' || type === 'schedule') {
      const d1 = new Date();
      const formatD = (dateObj) => {
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };
      const getDay = (dateObj) => {
        return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dateObj.getDay()];
      };
      const d1Str = formatD(d1);
      const d1Day = getDay(d1);
      const d2 = new Date(d1); d2.setDate(d2.getDate() + 1);
      const d2Str = formatD(d2);
      const d2Day = getDay(d2);

      // Get all active batch IDs to use as column headers
      const batchIds = batches.length > 0 ? batches.map(b => b.batchIdStr) : ['B-2025-A', 'N-2024-X'];
      const headersStr = `Date,DayOfWeek,StartTime,EndTime,${batchIds.join(',')}`;
      
      // Sample data for a few rows
      const row1Data = batchIds.map(id => `Physics(teacher@example.com)`).join(',');
      const row2Data = batchIds.map(id => `Chemistry(teacher@example.com)`).join(',');
      
      csvContent = `${headersStr}\n${d1Str},${d1Day},10:00,11:30,${row1Data}\n${d2Str},${d2Day},12:00,13:30,${row2Data}`;
      filename = "Sample_Master_Timetable.csv";
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDrag = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(prev => ({ ...prev, [type]: true }));
    } else if (e.type === "dragleave") {
      setDragActive(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [type]: false }));
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      if (type === 'student') setStudentFile(e.dataTransfer.files[0]);
      if (type === 'teacher') setTeacherFile(e.dataTransfer.files[0]);
      if (type === 'timetable') setTimetableFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e, type) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      if (type === 'student') setStudentFile(e.target.files[0]);
      if (type === 'teacher') setTeacherFile(e.target.files[0]);
      if (type === 'timetable') setTimetableFile(e.target.files[0]);
    }
  };

  const handleSubmitNewBatch = async (e) => {
    e.preventDefault();
    if (!batchName) return;
    setLoadingBatches(true);

    const formData = new FormData();
    formData.append("name", batchName);
    if (hod) formData.append("hod", hod);
    if (pipeline) formData.append("pipeline", pipeline);
    if (studentFile) formData.append("studentFile", studentFile);
    if (teacherFile) formData.append("teacherFile", teacherFile);
    if (timetableFile) formData.append("timetableFile", timetableFile);

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      await axios.post(API_BASE_URL + '/api/batches', formData, {
        headers: { 
          'X-Tenant-ID': tenantId
        }
      });
      toast.success("Batch created successfully");
      setShowNewBatchModal(false);
      setStudentFile(null);
      setTeacherFile(null);
      setTimetableFile(null);
      setBatchName("");
      setHod("");
      setPipeline("");
      fetchBatches(); // Refetch from DB
    } catch (err) {
      console.error(err);
      toast("Failed to create batch. Is the backend running?");
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleUploadSchedule = async (e) => {
    e.preventDefault();
    if (!scheduleFile) return;
    setUploadingSchedule(true);

    const formData = new FormData();
    formData.append("file", scheduleFile);

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      await axios.post(API_BASE_URL + '/api/batches/schedule/upload', formData, {
        headers: { 
          'X-Tenant-ID': tenantId
        }
      });
      toast("Timetable Schedule Successfully Uploaded!");
      setShowScheduleModal(false);
      setScheduleFile(null);
      fetchBatches(); 
      fetchTeachers();
    } catch (err) {
      console.error(err);
      toast("Failed to upload schedule. Ensure backend is configured for this route.");
    } finally {
      setUploadingSchedule(false);
    }
  };

  const handleAttendanceClick = (cls) => {
    if (!cls.batch) {
      toast.error("Cannot identify batch for this class.");
      return;
    }
    
    // Set class date if available, otherwise fallback to current date in modal
    if (cls.date) {
      setAttendanceDate(cls.date);
    }
    
    handleAttendanceBatchChange(cls.batch);
    setShowAttendanceModal(true);
  };

  const handleAttendanceBatchChange = async (batchId) => {
    const batch = batches.find(b => b.batchIdStr === batchId);
    setSelectedAttendanceBatch(batch);
    if (!batchId) {
      setAttendanceStudents([]);
      setAttendanceRecords({});
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      const res = await axios.get(`${API_BASE_URL}/api/batches/${batchId}/students`, { headers });
      setAttendanceStudents(res.data);
      
      const initialRecords = {};
      res.data.forEach(s => {
        initialRecords[s.email] = { status: 'present', remarks: '' };
      });
      setAttendanceRecords(initialRecords);
    } catch (err) {
      console.error(err);
      toast("Failed to fetch students for attendance.");
    }
  };

  const toggleAttendanceStatus = (email, status) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [email]: { ...prev[email], status }
    }));
  };

  const handleMarkAll = (status) => {
    setAttendanceRecords(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(email => {
        next[email].status = status;
      });
      return next;
    });
  };

  const handleSubmitAttendance = async () => {
    if (!selectedAttendanceBatch || !attendanceDate) {
      toast("Please select batch and date.");
      return;
    }

    setSubmittingAttendance(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      
      const payload = {
        batchIdStr: selectedAttendanceBatch.batchIdStr,
        date: attendanceDate,
        records: Object.entries(attendanceRecords).map(([email, data]) => ({
          studentEmail: email,
          status: data.status,
          remarks: data.remarks
        }))
      };

      await axios.post(`${API_BASE_URL}/api/students/attendance/batch`, payload, { headers });
      toast.success(`Attendance successfully logged for ${attendanceDate}`);
      setShowAttendanceModal(false);
      setSelectedAttendanceBatch(null);
      setAttendanceStudents([]);
      setAttendanceRecords({});
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit attendance records.");
    } finally {
      setSubmittingAttendance(false);
    }
  };

  const renderUploadZone = (title, type, file, description) => (
    <div className="space-y-3 bg-slate-900/50 p-5 rounded-2xl border border-slate-800">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-300">{title}</h3>
        <button 
          type="button" 
          onClick={() => downloadSample(type)}
          className="text-xs font-semibold text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
          Sample Format
        </button>
      </div>
      
      <div 
        className={`relative hover:cursor-pointer border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center transition-all ${
          dragActive[type] ? 'border-violet-500 bg-violet-500/5 scale-[1.02]' : 'border-slate-700 hover:border-slate-500 bg-slate-950/50'
        }`}
        onDragEnter={(e) => handleDrag(e, type)}
        onDragLeave={(e) => handleDrag(e, type)}
        onDragOver={(e) => handleDrag(e, type)}
        onDrop={(e) => handleDrop(e, type)}
      >
        <input 
          type="file" 
          accept=".xlsx, .xls, .csv" 
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onChange={(e) => handleFileChange(e, type)}
        />
        
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors ${file ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
          {file ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
          )}
        </div>

        <h4 className="text-sm font-semibold text-slate-200 mb-1 text-center">
          {file ? file.name : `Drag Excel for ${title}`}
        </h4>
        
        <p className="text-xs text-slate-500 text-center max-w-sm">
          {file ? `${(file.size / 1024).toFixed(2)} KB ready for upload.` : description}
        </p>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400">
            Batch & Schedule Hub
          </h1>
          <p className="mt-2 text-lg text-slate-400 max-w-2xl">
            Orchestrate live classes, manage student rosters, and assign teaching staff across multiple competitive batches.
          </p>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={() => setShowNewBatchModal(true)}
            className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium transition-colors border border-slate-700 flex items-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>
            New Batch
          </button>
          <button 
            onClick={() => setShowScheduleModal(true)}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white font-medium transition-all shadow-lg shadow-violet-500/20 flex items-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
            Schedule Class
          </button>
          <button 
            onClick={() => setShowAiModal(true)}
            className="px-5 py-2.5 rounded-xl bg-violet-500/10 hover:bg-violet-500/20 text-violet-400 font-medium transition-colors border border-violet-500/20 flex items-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="m4.93 4.93 1.41 1.41"></path><path d="m17.66 17.66 1.41 1.41"></path><path d="M2 12h2"></path><path d="M20 12h2"></path><path d="m6.34 17.66-1.41 1.41"></path><path d="m19.07 4.93-1.41 1.41"></path></svg>
            AI Schedule
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Live Classes Calendar */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Conflicts Panel Placeholder / Active View */}
          <div className={`rounded-2xl p-4 sm:p-5 shadow-sm animate-in fade-in slide-in-from-top-4 border ${scheduleConflicts.length > 0 ? 'bg-rose-950/20 border-rose-900/50' : 'bg-slate-900/40 border-slate-800'}`}>
            <div className={`flex items-center gap-2 mb-4 ${scheduleConflicts.length > 0 ? 'text-rose-400' : 'text-slate-400'}`}>
              {scheduleConflicts.length > 0 ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              )}
              <h3 className="font-bold text-lg tracking-tight">Timetable Conflicts</h3>
              {scheduleConflicts.length > 0 ? (
                <span className="bg-rose-500/20 text-rose-300 text-[10px] font-bold px-2 py-0.5 rounded-full ml-2">{scheduleConflicts.length} Issues</span>
              ) : (
                 <span className="bg-emerald-500/10 text-emerald-400 text-[10px] font-bold px-2 py-0.5 rounded-full ml-2 border border-emerald-500/20">All Clear</span>
              )}
            </div>
            
            {scheduleConflicts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[250px] overflow-y-auto custom-scrollbar pr-1">
                {scheduleConflicts.map((conf) => (
                  <div key={conf.id} className="bg-rose-900/10 border border-rose-500/20 rounded-xl p-3 flex flex-col gap-1.5 hover:bg-rose-900/20 transition-colors">
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ${conf.severity === 'critical' ? 'bg-rose-500/20 text-rose-400' : 'bg-orange-500/20 text-orange-400'}`}>{conf.type}</span>
                      <div className="text-[10px] font-semibold text-slate-400 flex items-center gap-1.5">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                        {conf.date.substring(0, 15)} &nbsp; {conf.time.split('-')[0]?.trim() || conf.time}
                      </div>
                    </div>
                    <p className="font-bold text-sm text-slate-200 mt-1">{conf.title}</p>
                    <p className="text-xs text-slate-400 leading-relaxed">{conf.message}</p>
                  </div>
                ))}
              </div>
            ) : (
               <div className="flex flex-col items-center justify-center py-6 text-slate-500">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-2 opacity-50 text-emerald-500/50"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                  <p className="text-sm font-medium text-slate-400 text-center">No scheduling conflicts detected</p>
                  <p className="text-xs mt-1 max-w-[280px] text-center opacity-70">The timetable engine shows zero clashes across all active batches and scheduled faculty.</p>
               </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button onClick={() => changeLiveDate(-1)} className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors border border-slate-700 shadow-sm active:scale-95">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
              </button>
              <h2 className="text-xl font-semibold text-slate-200 min-w-[200px] text-center tracking-tight">
                {selectedLiveDate.toDateString() === new Date().toDateString() 
                  ? "Today's Live Timetable" 
                  : `${selectedLiveDate.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})} Timetable`}
              </h2>
              <button onClick={() => changeLiveDate(1)} className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors border border-slate-700 shadow-sm active:scale-95">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </button>
            </div>
            <div className="flex items-center p-1 bg-slate-900 rounded-lg border border-slate-800 self-start sm:self-auto shadow-inner">
              <button 
                onClick={() => setLiveClassViewMode('all')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${liveClassViewMode === 'all' ? 'bg-emerald-500/20 text-emerald-400 shadow-sm' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800/50'}`}
              >
                All
              </button>
              <button 
                onClick={() => setLiveClassViewMode('batch')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${liveClassViewMode === 'batch' ? 'bg-emerald-500/20 text-emerald-400 shadow-sm' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800/50'}`}
              >
                By Batch
              </button>
              <button 
                onClick={() => setLiveClassViewMode('teacher')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${liveClassViewMode === 'teacher' ? 'bg-emerald-500/20 text-emerald-400 shadow-sm' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800/50'}`}
              >
                By Teacher
              </button>
            </div>
          </div>
          
          <div className="space-y-4">
            {(loadingTeachers || loadingBatches) ? (
              renderLoadingState("Syncing Timetable")
            ) : liveClasses.length === 0 ? (
              <div className="p-8 text-center border border-dashed border-slate-800 rounded-2xl bg-slate-900/40 text-slate-500">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3 opacity-50"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                <p>No live classes scheduled for today.</p>
              </div>
            ) : liveClassViewMode === 'all' ? (
              liveClasses.map((cls, idx) => renderClassCard(cls, idx))
            ) : (
              Object.entries(
                liveClasses.reduce((acc, cls) => {
                  const key = liveClassViewMode === 'batch' ? (cls.batch || 'Unassigned Batch') : (cls.teacher || 'Unassigned Teacher');
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(cls);
                  return acc;
                }, {})
              ).map(([group, classes], gIdx) => (
                <div key={gIdx} className="mb-6 last:mb-0 border border-slate-800/60 rounded-2xl bg-slate-900/20 overflow-hidden shadow-sm">
                  <div className="bg-slate-900/80 px-5 py-3 border-b border-slate-800/80 flex items-center justify-between backdrop-blur-sm">
                    <h3 className="font-bold text-emerald-400 uppercase tracking-wider text-sm flex items-center gap-2">
                       {liveClassViewMode === 'batch' ? (
                         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                       ) : (
                         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
                       )}
                       {group}
                    </h3>
                    <span className="text-[10px] font-bold text-slate-400 border border-slate-700 bg-slate-800/50 px-2 py-0.5 rounded-full">{classes.length} {classes.length === 1 ? 'Slot' : 'Slots'}</span>
                  </div>
                  <div className="p-3 sm:p-5 space-y-3 bg-slate-950/30">
                    {classes.map((cls, idx) => renderClassCard(cls, `${gIdx}-${idx}`))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Column: Active Batches */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-200">Database Batches</h2>
            <button className="text-sm font-medium text-fuchsia-400 hover:text-fuchsia-300">View All</button>
          </div>
          
          <div className="grid gap-4">
            {loadingBatches ? (
              renderLoadingState("Fetching Batches")
            ) : batches.length === 0 ? (
               <div className="p-12 border-2 border-dashed border-slate-800 rounded-3xl bg-slate-900/20 text-center text-slate-500 font-medium italic">
                  No active batches found.
               </div>
             ) : (() => {
                return batches.map((batch, idx) => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
                  const hasTomorrowSchedule = Array.isArray(batch.masterSchedule) && batch.masterSchedule.some(s => s.date === tomorrowStr);

                  return (
              <div 
                key={idx} 
                onClick={() => handleBatchClick(batch)}
                className="p-5 rounded-2xl bg-slate-900/40 border border-slate-800 hover:border-violet-500/50 hover:bg-slate-800/50 cursor-pointer transition-all active:scale-[0.98] group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-200 group-hover:text-violet-300 transition-colors">{batch.name}</h3>
                    <p className="text-xs text-slate-500 font-mono mt-1">{batch.batchIdStr}</p>
                  </div>
                  <span className="px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-bold uppercase tracking-wider">
                    {batch.status}
                  </span>
                </div>
                
                <div className="flex flex-col gap-2 text-sm border-t border-slate-800 pt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-400 border-r border-slate-700 pr-2">
                       <span>{batch.enrolledCount || 0} Students</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400 pl-2">
                       <span>{batch.hod || 'Unassigned Leader'}</span>
                    </div>
                  </div>
                  
                  {/* File Upload Status Render */}
                  {/* File Upload Status Render */}
                  <div className="text-xs font-medium text-slate-500 mt-2 space-y-1.5 bg-slate-950/30 p-3 rounded-lg border border-slate-800/60">
                      {/* Today's Schedule Snapshot Removed */}

                     <p className="flex items-center gap-2">
                        {batch.enrolledCount > 0 ? <span className="text-emerald-400 flex items-center gap-1.5"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Students Assigned</span> : <span className="text-rose-400 flex items-center gap-1.5"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> No student data</span>}
                     </p>
                      <p className="flex items-center gap-2">
                        {batch.teachersListFilename ? <span className="text-emerald-400 flex items-center gap-1.5"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Faculty Assigned</span> : <span className="text-rose-400 flex items-center gap-1.5"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> No teacher data</span>}
                      </p>
                     <p className="flex items-center gap-2">
                        {hasTomorrowSchedule ? <span className="text-emerald-400 flex items-center gap-1.5"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Tomorrow Scheduled</span> : <span className="text-rose-400 flex items-center gap-1.5"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> No Schedule for Tomorrow</span>}
                     </p>
                   </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-6 flex flex-wrap gap-2 pt-4 border-t border-slate-800/60">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBatchClick(batch);
                        }}
                        className="flex-1 py-2 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center justify-center gap-1.5 border border-slate-700 hover:border-slate-500"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                        Roster
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedScheduleBatch(batch);
                        }}
                        title="View Weekly Schedule"
                        className="flex-1 py-2 px-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center justify-center gap-1.5"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                        Schedule
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBatch(batch);
                        }}
                        title="Delete Batch"
                        className="p-2 rounded-lg border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 transition-colors flex items-center justify-center"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                      </button>
                    </div>
                </div>
              </div>
                  );
                });
            })()}
          </div>
          
          {/* Quick Stats */}
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-fuchsia-900/20 to-violet-900/20 border border-fuchsia-500/20 grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold text-fuchsia-400">{batches.length}</p>
              <p className="text-[10px] uppercase font-semibold text-slate-500 mt-1">Total Batches</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-violet-400">
                {batches.reduce((acc, b) => acc + (b.enrolledCount || 0), 0)}
              </p>
              <p className="text-[10px] uppercase font-semibold text-slate-500 mt-1">Enrolled Kids</p>
            </div>
          </div>
        </div>

      </div>

      {showScheduleModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4 duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 w-full max-w-md shadow-2xl relative animate-in zoom-in-95">
            <h2 className="text-2xl font-bold text-white mb-2">Upload Class Timetable</h2>
            <div className="flex justify-between items-start mb-6">
              <p className="text-slate-400 text-sm">
                Select an Excel/CSV file to bulk schedule classes across batches.
              </p>
            </div>
            
            <div className="flex items-center justify-between mb-2">
               <p className="text-xs text-slate-500 font-mono bg-slate-950 p-2 rounded">Format: Date, Day, Time, [Batch_ID_1], ... | Cell: Subject(Email)</p>
               <button onClick={() => downloadSample('schedule')} className="text-sm font-semibold text-emerald-400 hover:text-emerald-300">Download Sample</button>
            </div>
            
            <form onSubmit={handleUploadSchedule} className="space-y-6">
              <div className="relative group">
                <div className="absolute inset-0 bg-violet-500/10 rounded-2xl border-2 border-dashed border-violet-500/30 group-hover:border-violet-500/50 transition-colors"></div>
                <input 
                  type="file" 
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={(e) => setScheduleFile(e.target.files[0])}
                  className="relative z-10 w-full h-32 opacity-0 cursor-pointer"
                  required
                />
                <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center text-violet-500">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                  <span className="font-medium text-sm">{scheduleFile ? scheduleFile.name : 'Click or drop file here'}</span>
                </div>
              </div>
              
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowScheduleModal(false)} className="flex-1 py-3 rounded-xl border border-slate-700 font-semibold text-slate-300 hover:bg-slate-800 transition-colors">Cancel</button>
                <button type="submit" disabled={uploadingSchedule || !scheduleFile} className="flex-[2] py-3 rounded-xl bg-violet-500 font-semibold text-white hover:bg-violet-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/20">
                  {uploadingSchedule ? 'Scheduling...' : 'Upload Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW BATCH & TIMETABLE MODAL */}
      {showNewBatchModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[95vh] animate-in zoom-in-95 duration-200 overflow-hidden">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-slate-800/60 bg-slate-900 sticky top-0 z-10">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-violet-400"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg></span>
                  Create Batch Profile
                </h2>
                <p className="text-slate-400 text-sm mt-1">Configure batch identity and bulk upload participants via Excel.</p>
              </div>
              <button onClick={() => setShowNewBatchModal(false)} className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            {/* Modal Body (Scrollable) */}
            <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar">
              <form id="batch-form" onSubmit={handleSubmitNewBatch} className="space-y-8">
                
                {/* 1. Core Info */}
                <div className="bg-slate-950/50 p-6 rounded-2xl border border-slate-800 space-y-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-violet-400 mb-3">Primary Batch Name</label>
                    <input 
                      type="text" 
                      value={batchName}
                      onChange={(e) => setBatchName(e.target.value)}
                      placeholder="e.g. JEE Apex 2026" 
                      className="w-full bg-slate-950 border border-slate-700/60 rounded-xl p-4 text-white font-semibold text-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none transition-all placeholder:text-slate-700 placeholder:font-normal" 
                      required 
                      disabled={loadingBatches}
                    />
                  </div>
                  <p className="text-slate-500 text-xs">
                    * A unique system ID (e.g. JEE-APEX-2026) will be automatically generated from the prefix.
                  </p>
                </div>
                
                {/* Optional Configuration Toggle */}
                <details className="group border border-slate-800/60 rounded-2xl overflow-hidden bg-slate-900/30">
                  <summary className="p-4 cursor-pointer hover:bg-slate-800/40 transition-colors list-none flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-400">Advanced Configuration (Profiles & Files)</span>
                    <svg className="w-5 h-5 text-slate-600 group-open:rotate-180 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                  </summary>
                  <div className="p-6 space-y-8 border-t border-slate-800/50 animate-in slide-in-from-top-2 duration-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                       <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Assign Lead Teacher / HOD</label>
                          <select 
                            value={hod}
                            onChange={(e) => setHod(e.target.value)}
                            disabled={loadingBatches}
                            className="w-full bg-slate-950 border border-slate-700/60 rounded-xl p-3 text-slate-200 focus:border-violet-500 outline-none transition-all cursor-pointer"
                          >
                            <option value="">-- Select Instructor --</option>
                            {allTeachers.map(t => (
                              <option key={t.id} value={t.name}>{t.name} ({t.teacherEmail})</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Student Pipeline Mode</label>
                          <select 
                            value={pipeline}
                            onChange={(e) => setPipeline(e.target.value)}
                            disabled={loadingBatches}
                            className="w-full bg-slate-950 border border-slate-700/60 rounded-xl p-3 text-slate-200 focus:border-violet-500 outline-none transition-all cursor-pointer"
                          >
                            <option value="">-- Select Mode --</option>
                            <option>Parse from Uploaded Excel (Strict matching)</option>
                            <option>Parse from Uploaded Excel (Flex matching)</option>
                          </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {renderUploadZone("Students List", "student", studentFile, "Upload student ID, Name, Contact mapping.")}
                      {renderUploadZone("Teachers List", "teacher", teacherFile, "Upload teacher ID, Subject, and Role designation.")}
                      {renderUploadZone("Weekly Timetable", "timetable", timetableFile, "Upload Day, Time Slot, and Subjects mapping.")}
                    </div>
                  </div>
                </details>

              </form>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-slate-800/60 bg-slate-900 flex justify-end gap-3 sticky bottom-0 z-10">
              <button 
                type="button" 
                onClick={() => setShowNewBatchModal(false)}
                className="px-6 py-2.5 rounded-xl border border-slate-700 bg-transparent hover:bg-slate-800 text-slate-300 font-medium transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit"
                form="batch-form"
                disabled={!batchName || loadingBatches}
                className={`px-8 py-2.5 rounded-xl font-bold transition-all shadow-lg flex items-center gap-2 ${
                  (batchName && !loadingBatches)
                    ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white shadow-violet-500/20' 
                    : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                }`}
              >
                {loadingBatches ? 'Processing...' : 'Create Batch Profile'}
                {!loadingBatches && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* BATCH DETAILS MODAL (Students & Teachers) */}
      {selectedBatchDetails && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200 overflow-hidden">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-slate-800/60 bg-slate-900 sticky top-0 z-10">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-violet-400">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  </span>
                  {selectedBatchDetails.name} Roster
                </h2>
                <p className="text-slate-400 text-sm mt-1 font-mono">{selectedBatchDetails.batchIdStr} • {batchStudents.length} Students, {batchTeachers.length} Teachers</p>
              </div>
              <button 
                onClick={() => setSelectedBatchDetails(null)} 
                className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar flex-1 bg-slate-950/30">
              {detailsLoading ? (
                <div className="flex items-center justify-center h-48 space-x-3 text-violet-400">
                   <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                   <span className="font-medium animate-pulse">Loading live roster from database...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  
                  {/* Students Column */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400 border-b border-slate-800 pb-3 flex justify-between">
                      Mapped Students 
                      <span className="bg-slate-800 text-slate-300 px-2 rounded">{batchStudents.length}</span>
                    </h3>
                    
                    <div className="flex gap-2">
                      <select 
                        value={selectedAddStudent} 
                        onChange={(e) => setSelectedAddStudent(e.target.value)}
                        className="flex-1 bg-slate-950 border border-slate-800 text-slate-300 text-xs rounded-lg px-3 py-2 outline-none focus:border-violet-500"
                      >
                        <option value="">{(Array.isArray(allStudents) ? allStudents : []).filter(s => !s.batchIdStr && !batchStudents.find(bs => bs.id === s.id)).length === 0 ? "Add Student First" : "-- Select Unassigned Student --"}</option>
                        {(Array.isArray(allStudents) ? allStudents : []).filter(s => !s.batchIdStr && !batchStudents.find(bs => bs.id === s.id)).map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
                        ))}
                      </select>
                      <button 
                        onClick={() => handleAddMember('student')}
                        disabled={!selectedAddStudent || actionLoading}
                        className="px-3 py-1.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors"
                      >
                        Add
                      </button>
                    </div>

                    <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar pr-1">
                       {batchStudents.length === 0 ? (
                         <p className="text-xs text-slate-500 italic py-4">No students mapped securely in the DB.</p>
                       ) : (
                         batchStudents.map((s, idx) => (
                           <div key={idx} className="group flex items-center justify-between p-3 rounded-xl bg-slate-900 border border-slate-800/80 hover:border-violet-500/50 transition-colors">
                             <div className="flex-1 min-w-0 pr-3">
                               <p className="font-semibold text-slate-200 text-sm truncate">{s.name}</p>
                               <p className="text-[10px] sm:text-xs text-slate-500 truncate">{s.email}</p>
                             </div>
                             <div className="flex items-center gap-2 shrink-0">
                               <span className="text-[10px] font-bold tracking-wider uppercase text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">{s.status}</span>
                               <button 
                                 onClick={() => handleRemoveMember('student', s.id)}
                                 disabled={actionLoading}
                                 className="opacity-0 group-hover:opacity-100 p-1.5 text-rose-400 hover:bg-rose-500/20 hover:text-rose-300 rounded-md transition-all border border-transparent hover:border-rose-500/30"
                                 title="Remove Student from Batch"
                               >
                                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                               </button>
                             </div>
                            </div>
                          ))
             )}
                    </div>
                  </div>                  {/* Teachers Column */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400 border-b border-slate-800 pb-3 flex justify-between">
                      Assigned Faculty
                      <span className="bg-slate-800 text-slate-300 px-2 rounded">{batchTeachers.length}</span>
                    </h3>
                    
                    <div className="flex gap-2">
                      <select 
                        value={selectedAddTeacher} 
                        onChange={(e) => setSelectedAddTeacher(e.target.value)}
                        className="flex-1 bg-slate-950 border border-slate-800 text-slate-300 text-xs rounded-lg px-3 py-2 outline-none focus:border-violet-500"
                      >
                        <option value="">{(Array.isArray(allTeachers) ? allTeachers : []).filter(t => !batchTeachers.find(bt => bt.teacherEmail === t.teacherEmail)).length === 0 ? "Add Teacher First" : "-- Select Unassigned Teacher --"}</option>
                        {(Array.isArray(allTeachers) ? allTeachers : []).filter(t => !batchTeachers.find(bt => bt.teacherEmail === t.teacherEmail)).map(t => (
                          <option key={t.id} value={t.teacherEmail}>{t.name} ({t.teacherEmail})</option>
                        ))}
                      </select>
                      <button 
                        onClick={() => handleAddMember('teacher')}
                        disabled={!selectedAddTeacher || actionLoading}
                        className="px-3 py-1.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors"
                      >
                        Add
                      </button>
                    </div>

                    <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar pr-1">
                       {batchTeachers.length === 0 ? (
                         <p className="text-xs text-slate-500 italic py-4">No teachers mapped to this batch.</p>
                       ) : (
                         batchTeachers.map((t, idx) => (
                           <div key={idx} className="group flex items-center justify-between p-3 rounded-xl bg-slate-900 border border-slate-800/80 hover:border-violet-500/50 transition-colors">
                             <div className="flex items-center gap-3 flex-1 min-w-0 pr-3">
                               <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-violet-400 shrink-0 border border-slate-700">
                                 {t.initials}
                               </div>
                               <div className="min-w-0">
                                 <p className="font-semibold text-slate-200 text-sm truncate">{t.name}</p>
                                 <p className="text-[10px] sm:text-xs text-slate-500 truncate">{t.department} Department</p>
                               </div>
                             </div>
                             <div className="shrink-0">
                               <button 
                                 onClick={() => handleRemoveMember('teacher', t.teacherEmail)}
                                 disabled={actionLoading}
                                 className="opacity-0 group-hover:opacity-100 p-1.5 text-rose-400 hover:bg-rose-500/20 hover:text-rose-300 rounded-md transition-all border border-transparent hover:border-rose-500/30"
                                 title="Remove Teacher from Batch"
                               >
                                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                               </button>
                             </div>
                           </div>
                         ))
                       )}
                    </div>
                  </div>

                </div>
              )}
            </div>
            
            <div className="p-5 border-t border-slate-800/60 bg-slate-900 flex justify-end">
              <button 
                onClick={() => setSelectedBatchDetails(null)}
                className="px-6 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-colors"
              >
                Close Panel
              </button>
            </div>

          </div>
        </div>
      )}
      {/* BATCH SCHEDULE MODAL */}
      {selectedScheduleBatch && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-[98vw] max-w-[1500px] shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200 overflow-hidden mx-auto">
            
            <div className="flex justify-between items-center p-6 border-b border-slate-800/60 bg-slate-900 sticky top-0 z-10">
              <div>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <span className="text-emerald-400">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                    </span>
                    Weekly Timetable for {selectedScheduleBatch.name}
                  </h2>
                  <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-1 border border-slate-700 w-fit">
                    <button 
                      onClick={() => {
                        const newDate = new Date(selectedScheduleWeekDate);
                        newDate.setDate(newDate.getDate() - 7);
                        setSelectedScheduleWeekDate(newDate);
                      }} 
                      className="p-1.5 rounded-md hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                      title="Previous Week"
                    >
                       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                    </button>
                    <span className="text-sm font-semibold text-slate-200 min-w-[120px] text-center">
                       Week of {getDateForDayInSelectedWeek('Monday', selectedScheduleWeekDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <button 
                      onClick={() => {
                        const newDate = new Date(selectedScheduleWeekDate);
                        newDate.setDate(newDate.getDate() + 7);
                        setSelectedScheduleWeekDate(newDate);
                      }} 
                      className="p-1.5 rounded-md hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                      title="Next Week"
                    >
                       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                    </button>
                  </div>
                </div>
                <p className="text-slate-400 text-sm mt-2 font-mono">{selectedScheduleBatch.batchIdStr} • Master Schedule</p>
              </div>
              <button onClick={() => setSelectedScheduleBatch(null)} className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar flex-1 bg-slate-950/30">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, dIdx) => (
                     <div key={day} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-lg flex flex-col">
                       <div className="bg-slate-800/50 p-2.5 sm:p-3 border-b border-slate-800 flex justify-between items-start sm:items-center">
                         <div className="flex flex-col">
                           <h3 className="font-bold text-slate-200 text-sm whitespace-nowrap overflow-hidden">{day}</h3>
                           <span className="text-[10px] text-slate-400 font-medium tracking-wide">
                             {getDateForDayInSelectedWeek(day, selectedScheduleWeekDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                           </span>
                         </div>
                         <button 
                           onClick={() => {
                             setEditableSchedule(prev => ({
                               ...prev,
                               [day]: [...prev[day], { id: Date.now().toString(), time: '', subject: '', teacher: '' }]
                             }));
                           }}
                           title="Add Slot" 
                           className="text-emerald-400 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 bg-emerald-500/10 hover:bg-emerald-500/20 px-2 py-1 rounded transition-colors"
                         >
                           <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                           Add
                         </button>
                       </div>
                       <div className="p-3 space-y-3 flex-1 flex flex-col">
                         {editableSchedule[day].map((item, idx) => (
                           <div key={item.id} className="p-3 rounded-xl bg-slate-950/50 border border-slate-800/60 hover:border-emerald-500/30 transition-colors group relative overflow-hidden flex flex-col gap-1.5 focus-within:border-emerald-500/60 focus-within:ring-1 focus-within:ring-emerald-500/30">
                             <div className={`absolute left-0 top-0 bottom-0 w-1 ${idx % 2 === 0 ? 'bg-violet-500/50 group-hover:bg-violet-400' : 'bg-fuchsia-500/50 group-hover:bg-fuchsia-400'} transition-colors`}></div>
                             <button 
                               onClick={() => {
                                 setEditableSchedule(prev => ({
                                   ...prev,
                                   [day]: prev[day].filter((_, i) => i !== idx)
                                 }));
                               }}
                               className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 text-rose-400 hover:bg-rose-500/20 p-0.5 rounded transition-all shrink-0 bg-slate-900/80 backdrop-blur-sm border border-slate-800/50 hover:border-rose-500/30 shadow-sm"
                               title="Remove Slot"
                             >
                               <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                             </button>
 
                             <div className="flex items-center gap-0 w-full mb-1 bg-slate-900/50 border border-slate-700/60 rounded p-1 focus-within:border-emerald-500/50 transition-colors">
                               <input 
                                 type="time"
                                 value={(item.time || "10:00 - 11:30").split(' - ')[0] || ""}
                                 onChange={(e) => handleScheduleChange(day, idx, 'time', `${e.target.value} - ${(item.time || "10:00 - 11:30").split(' - ')[1] || ""}`)}
                                 className="bg-transparent outline-none text-[9px] lg:text-[10px] font-bold text-slate-300 focus:text-emerald-300 cursor-pointer w-full text-center transition-colors [&::-webkit-calendar-picker-indicator]:hidden"
                               />
                               <span className="text-slate-500 text-[10px] font-bold shrink-0 opacity-50 px-0.5">-</span>
                               <input 
                                 type="time"
                                 value={(item.time || "10:00 - 11:30").split(' - ')[1] || ""}
                                 onChange={(e) => handleScheduleChange(day, idx, 'time', `${((item.time || "10:00 - 11:30").split(' - ')[0] || "")} - ${e.target.value}`)}
                                 className="bg-transparent outline-none text-[9px] lg:text-[10px] font-bold text-slate-300 focus:text-emerald-300 cursor-pointer w-full text-center transition-colors [&::-webkit-calendar-picker-indicator]:hidden"
                               />
                             </div>
                             
                             <select 
                               value={item.subject || ""}
                               onChange={(e) => handleScheduleChange(day, idx, 'subject', e.target.value)}
                               className="w-full bg-transparent outline-none text-sm font-semibold text-slate-200 focus:text-white appearance-none cursor-pointer mt-1"
                             >
                               <option value="" disabled className="bg-slate-900 text-slate-500">Select Subject</option>
                               {["Physics", "Chemistry", "Mathematics", "Biology", "Science", "English", "Social Studies", "Computer Science", "General Guidance"].map(s => (
                                 <option key={s} value={s} className="bg-slate-900 text-slate-300">{s}</option>
                               ))}
                             </select>
                             
                             <select 
                               value={item.teacher || ""}
                               onChange={(e) => handleScheduleChange(day, idx, 'teacher', e.target.value)}
                               className="w-full bg-transparent outline-none text-xs text-slate-500 focus:text-slate-300 appearance-none cursor-pointer mt-1"
                             >
                               <option value="" disabled className="bg-slate-900 text-slate-600">{allTeachers.length === 0 ? "Add Teacher First" : "Add Teacher"}</option>
                               {/* Add the initial value if it isn't in the DB array yet just so it renders properly in the demo */}
                               {allTeachers.map(t => (
                                 <option key={t.id} value={t.teacherEmail} className="bg-slate-900 text-slate-300">
                                   {t.name} ({t.department})
                                 </option>
                               ))}
                             </select>
                           </div>
                         ))}
                         {editableSchedule[day].length === 0 && (
                            <div className="text-center py-4 text-[10px] text-slate-600 italic">No classes</div>
                         )}
                       </div>
                     </div>
                  ))}
                </div>
            </div>
            
            <div className="p-5 border-t border-slate-800/60 bg-slate-900 flex justify-between items-center">
              <button 
                onClick={handleExportSchedule} 
                className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 flex items-center gap-1.5 transition-colors"
                title="Export schedule as CSV"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Export Schedule Sheet
              </button>
              <div className="flex gap-3">
                <button onClick={handleSaveSchedule} className="px-6 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 text-sm font-semibold transition-colors">
                  Save Changes
                </button>
                <button onClick={() => setSelectedScheduleBatch(null)} className="px-6 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-colors">
                  Close Schedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ATTENDANCE MODAL */}
      {showAttendanceModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200 overflow-hidden">
            
            <div className="flex justify-between items-center p-6 border-b border-slate-800/60 bg-slate-900 sticky top-0 z-10">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-emerald-400">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><polyline points="17 11 19 13 23 9"></polyline></svg>
                  </span>
                  Roll Call & Attendance
                </h2>
                <p className="text-slate-400 text-sm mt-1">Mark student presence for a specific batch and date.</p>
              </div>
              <button onClick={() => setShowAttendanceModal(false)} className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <div className="p-6 md:p-8 flex-1 overflow-y-auto custom-scrollbar bg-slate-950/30">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Target Batch</label>
                  <select 
                    value={selectedAttendanceBatch?.batchIdStr || ""}
                    onChange={(e) => handleAttendanceBatchChange(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-violet-500 outline-none transition-all cursor-pointer"
                  >
                    <option value="">{batches.length === 0 ? "Assign Batch First" : "-- Select Batch to Load Roster --"}</option>
                    {batches.map(b => (
                      <option key={b.batchIdStr} value={b.batchIdStr}>{b.name} ({b.batchIdStr})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Attendance Date</label>
                  <input 
                    type="date" 
                    value={attendanceDate}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-violet-500 outline-none transition-all cursor-pointer font-mono"
                  />
                </div>
              </div>

              {selectedAttendanceBatch && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                    <h3 className="text-sm font-bold text-slate-300">Student Roster ({attendanceStudents.length})</h3>
                    <div className="flex gap-2">
                       <button onClick={() => handleMarkAll('present')} className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-lg hover:bg-emerald-500/20 transition-all uppercase tracking-wider">All Present</button>
                       <button onClick={() => handleMarkAll('absent')} className="text-[10px] font-bold text-rose-400 bg-rose-500/10 border border-rose-500/20 px-3 py-1.5 rounded-lg hover:bg-rose-500/20 transition-all uppercase tracking-wider">All Absent</button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {attendanceStudents.length > 0 ? (
                      attendanceStudents.map((s, idx) => (
                        <div key={idx} className="bg-slate-900/40 border border-slate-800/80 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-violet-500/30 transition-all">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-200 truncate">{s.name}</p>
                            <p className="text-[10px] text-slate-500 font-mono truncate">{s.email}</p>
                          </div>
                          
                          <div className="flex items-center gap-1.5 bg-slate-950 p-1 rounded-xl border border-slate-800">
                             {['present', 'absent', 'late'].map((st) => (
                               <button
                                 key={st}
                                 onClick={() => toggleAttendanceStatus(s.email, st)}
                                 className={`px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                                   attendanceRecords[s.email]?.status === st 
                                     ? st === 'present' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' 
                                     : st === 'absent' ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/20'
                                     : 'bg-amber-500 text-white shadow-lg shadow-amber-500/20'
                                     : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'
                                 }`}
                               >
                                 {st}
                               </button>
                             ))}
                          </div>
                        </div>
                      ))
                    ) : (
                       <div className="p-12 text-center text-slate-500 italic">No students found for this batch. Select a batch above to load members.</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-800/60 bg-slate-900 flex justify-end gap-3 sticky bottom-0 z-10">
              <button 
                onClick={() => setShowAttendanceModal(false)}
                className="px-6 py-2.5 rounded-xl border border-slate-700 bg-transparent hover:bg-slate-800 text-slate-300 font-medium transition-colors"
                disabled={submittingAttendance}
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitAttendance}
                disabled={!selectedAttendanceBatch || attendanceStudents.length === 0 || submittingAttendance}
                className={`px-8 py-2.5 rounded-xl font-bold transition-all shadow-lg flex items-center gap-2 ${
                  (!submittingAttendance && selectedAttendanceBatch && attendanceStudents.length > 0)
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-emerald-500/20' 
                    : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                }`}
              >
                {submittingAttendance ? 'Logging Attendance...' : 'Confirm & Save Records'}
                {!submittingAttendance && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI GENERATE SCHEDULE MODAL */}
      {showAiModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-violet-500/30 rounded-3xl w-full max-w-2xl shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 overflow-hidden">
            <div className="p-8 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-violet-500/20 flex items-center justify-center text-violet-400 border border-violet-500/30">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="m4.93 4.93 1.41 1.41"></path><path d="m17.66 17.66 1.41 1.41"></path><path d="M2 12h2"></path><path d="M20 12h2"></path><path d="m6.34 17.66-1.41 1.41"></path><path d="m19.07 4.93-1.41 1.41"></path></svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">AI Schedule Generator</h2>
                  <p className="text-slate-400 text-sm">Automate your weekly timetable with smart constraints.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Schedule Start Date (Monday)</label>
                    <input 
                      type="date"
                      value={aiStartDate}
                      onChange={(e) => setAiStartDate(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-violet-500 outline-none transition-all"
                    />
                  </div>
                  <div className="flex items-end">
                    <p className="text-[10px] text-slate-500 italic pb-2">
                      * The generator will automatically map Monday-{'>'}Sunday to specific dates starting from this week.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Additional Constraints & Preferences</label>
                  <textarea 
                    value={aiConstraints}
                    onChange={(e) => setAiConstraints(e.target.value)}
                    placeholder="e.g. Need 1 extra Biology class for Batch NEET-A. Teacher Dr. Verma is on leave on Wednesday. Keep morning slots for Math."
                    className="w-full h-40 bg-slate-950 border border-slate-800 rounded-2xl p-4 text-slate-200 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none transition-all resize-none placeholder:text-slate-700"
                  />
                </div>

                <div className="bg-violet-500/5 border border-violet-500/10 rounded-xl p-4 flex gap-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-violet-400 shrink-0"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    The AI will analyze last week's schedule, teacher leaves, and your constraints to propose an optimized plan. You can review and edit before saving.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button 
                  onClick={() => setShowAiModal(false)}
                  className="flex-1 py-3 rounded-xl border border-slate-800 font-semibold text-slate-400 hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleGenerateAiSchedule}
                  disabled={isGenerating}
                  className="flex-[2] py-3 rounded-xl bg-violet-500 hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-white transition-all shadow-lg shadow-violet-500/20 flex items-center justify-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 14 7-7 7 7"></path><path d="m5 21 7-7 7 7"></path></svg>
                      Generate Optimized Schedule
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI MASTER PREVIEW MODAL */}
      {showAiPreviewModal && aiFullResults && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-violet-500/30 rounded-3xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200 overflow-hidden">
             <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-900 sticky top-0 z-10">
                <div>
                   <h2 className="text-xl font-bold text-white flex items-center gap-2">
                     <span className="p-1.5 rounded-lg bg-violet-500/20 text-violet-400">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
                     </span>
                     AI Master Schedule Preview
                   </h2>
                   <p className="text-xs text-slate-500 font-medium uppercase tracking-widest mt-1">Found {Object.values(aiFullResults).flat().length} Classes across {new Set(Object.values(aiFullResults).flat().map(c => c.batch)).size} Batches</p>
                </div>
                <button onClick={() => setShowAiPreviewModal(false)} className="text-slate-400 hover:text-white transition-colors">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
             </div>

             <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 custom-scrollbar">
                {Object.entries(aiFullResults).map(([day, classes]) => classes.length > 0 && (
                  <div key={day} className="space-y-4">
                    <h3 className="text-sm font-bold text-violet-400 uppercase tracking-[0.2em] flex items-center gap-4">
                      {day}
                      <div className="h-px bg-violet-500/10 flex-1"></div>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {classes.map((cls, idx) => (
                        <div key={idx} className="bg-slate-950/50 border border-slate-800/80 rounded-2xl p-4 space-y-3 hover:border-violet-500/20 transition-all group">
                          <div className="flex items-center justify-between">
                            <span className="px-2 py-0.5 rounded-md bg-slate-800 text-[10px] font-bold text-slate-400 group-hover:bg-violet-500/20 group-hover:text-violet-300 transition-colors">
                              {cls.startTime} - {cls.endTime}
                            </span>
                            <span className="text-[10px] font-bold text-fuchsia-400 truncate max-w-[100px]">{cls.batch}</span>
                          </div>
                          <div>
                            <p className="font-bold text-slate-200 group-hover:text-white transition-colors">{cls.subject}</p>
                            <p className="text-xs text-slate-500 truncate">{cls.teacher}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
             </div>

             <div className="p-6 border-t border-slate-800 bg-slate-900 flex justify-between items-center gap-4 sticky bottom-0 z-10">
                <p className="text-xs text-slate-500 max-w-sm italic">
                  * Review carefully. Applying will overwrite current schedules for these batches on the week of <strong>{aiStartDate}</strong>.
                </p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowAiPreviewModal(false)}
                    className="px-6 py-2.5 rounded-xl border border-slate-700 font-semibold text-slate-400 hover:bg-slate-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleApplyAllAiSchedules}
                    disabled={isApplyingAll}
                    className="px-10 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 font-bold text-white shadow-lg shadow-violet-500/20 transition-all flex items-center gap-2"
                  >
                    {isApplyingAll ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Finalizing...
                      </>
                    ) : 'Finalize & Apply All'}
                  </button>
                </div>
             </div>
          </div>
        </div>
      )}

    </div>
  );
}

